import { addDays, differenceInCalendarDays, startOfDay } from 'date-fns';

import { getDataViewRuntimeLocale } from '../i18n.js';

const dateFormatter = (
  options: Intl.DateTimeFormatOptions,
  locale = getDataViewRuntimeLocale()
) => new Intl.DateTimeFormat(locale, options);

export const formatDateGroupDayLabel = (
  value: Date,
  locale = getDataViewRuntimeLocale()
): string =>
  dateFormatter(
    {
      day: 'numeric',
      month: 'long',
      weekday: 'long',
    },
    locale
  ).format(value);

export const formatDateGroupMonthLabel = (
  value: Date,
  locale = getDataViewRuntimeLocale()
): string =>
  dateFormatter({ month: 'long', year: 'numeric' }, locale).format(value);

export const formatDateGroupYearLabel = (
  value: Date,
  locale = getDataViewRuntimeLocale()
): string => dateFormatter({ year: 'numeric' }, locale).format(value);

export const formatDateGroupRangeLabel = (
  start: Date,
  end: Date,
  locale = getDataViewRuntimeLocale()
): string => {
  const formatter = dateFormatter(
    { day: 'numeric', month: 'short', year: 'numeric' },
    locale
  );
  return typeof formatter.formatRange === 'function'
    ? formatter.formatRange(start, end)
    : `${formatter.format(start)} – ${formatter.format(end)}`;
};

export const formatRelativeDateGroupLabel = (
  value: Date,
  today = new Date(),
  locale = getDataViewRuntimeLocale()
): string => {
  const normalizedValue = startOfDay(value);
  const normalizedToday = startOfDay(today);
  const daysDiff = differenceInCalendarDays(normalizedValue, normalizedToday);

  if (daysDiff >= -1 && daysDiff <= 1) {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
      daysDiff,
      'day'
    );
  }
  if (daysDiff > 1 && daysDiff <= 7) {
    return formatDateGroupRangeLabel(
      addDays(normalizedToday, 2),
      addDays(normalizedToday, 7),
      locale
    );
  }
  if (daysDiff > 7 && daysDiff <= 30) {
    return formatDateGroupRangeLabel(
      addDays(normalizedToday, 8),
      addDays(normalizedToday, 30),
      locale
    );
  }
  if (daysDiff < -1 && daysDiff >= -7) {
    return formatDateGroupRangeLabel(
      addDays(normalizedToday, -7),
      addDays(normalizedToday, -2),
      locale
    );
  }
  if (daysDiff < -7 && daysDiff >= -30) {
    return formatDateGroupRangeLabel(
      addDays(normalizedToday, -30),
      addDays(normalizedToday, -8),
      locale
    );
  }
  return formatDateGroupMonthLabel(normalizedValue, locale);
};
